/**
 * Where Zoho sends the admin back after the consent screen (spec 20).
 *
 * The URL carries `code` and `state` on success, or `error` if the admin said
 * no. The page posts the pair to the API with the admin's own session. The
 * session survives the round trip through Zoho because the refresh token is in
 * sessionStorage, which a same-tab navigation keeps.
 *
 * This path must match FITZONE_ZOHO_REDIRECT_URI exactly.
 */

import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button, Card, CardHeader, ErrorNotice, Page } from "../components/primitives";
import { completeZohoConnect } from "../lib/api/mail";
import { useMutation } from "../lib/hooks";

export function EmailCallbackScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = params.get("code");
  const state = params.get("state");
  const zohoError = params.get("error");

  const complete = useMutation((c: string, s: string) => completeZohoConnect(c, s), {
    onSuccess: (result) =>
      navigate("/settings/email", { replace: true, state: { notice: result.message } }),
  });

  // Zoho's code is single-use. StrictMode runs effects twice in development,
  // so without this guard the second POST would fail and replace the success.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !code || !state || zohoError) return;
    started.current = true;
    void complete.run(code, state);
  }, [code, state, zohoError, complete]);

  const problem =
    zohoError === "access_denied"
      ? "Access wasn't approved in Zoho, so no mailbox was connected."
      : zohoError
        ? "Zoho didn't complete the connection."
        : !code || !state
          ? "This page was opened without the details Zoho sends back."
          : null;

  return (
    <Page>
      <Card>
        <CardHeader title="Connecting Zoho Mail" />
        {problem ? (
          <p style={{ margin: 0 }}>{problem}</p>
        ) : complete.error ? (
          <ErrorNotice error={complete.error} />
        ) : (
          <p style={{ margin: 0 }}>Finishing the connection…</p>
        )}
        {problem || complete.error ? (
          <div className="row row--tight" style={{ marginTop: 12 }}>
            <Button onClick={() => navigate("/settings/email", { replace: true })}>
              Back to email settings
            </Button>
          </div>
        ) : null}
      </Card>
    </Page>
  );
}
